/* Custom Reaven — Free Gift automatico.
 *
 * Da una soglia in su (impostazione del tema, nella valuta del negozio) il carrello
 * contiene UNA riga del prodotto Free Gift a 0; sotto la soglia nessuna. Il cliente
 * non puo' aggiungerlo ne' toglierlo: lo gestisce solo questo file.
 *
 * Regola (identica a snippets/free-gift-dati.liquid):
 *   contano le righe che non sono il regalo, richiedono spedizione e non sono gift
 *   card, con final_line_price; diritto se ce n'e' almeno una e la somma >= soglia.
 *   Il regalo deve costare davvero 0 ed essere disponibile, altrimenti si toglie.
 *
 * Valute diverse da quella del negozio: la soglia equivalente si calcola al cambio
 * del sito (Shopify.currency.rate), arrotondata all'unita' per eccesso, e si salva
 * nel carrello come attributi nascosti _fg_soglia/_fg_valuta, cosi' il Liquid
 * (barra e riga regalo) usa lo stesso numero. Se la pagina e' in una valuta diversa
 * da quella del carrello (scheda rimasta aperta dopo un cambio di paese) la soglia
 * e' sconosciuta e il regalo non si tocca.
 *
 * Come resta coerente:
 *   - stato sempre riletto da /cart.js, mai dedotto da risposte parziali;
 *   - scritture assolute (quantita' 1 o 0) con /cart/update.js, per chiave di riga;
 *   - un giro alla volta, i trigger si fondono, massimo 3 passaggi per giro;
 *   - freno: 6 scritture in 20 s fermano lo script per 2 minuti, poi riprova da solo;
 *   - aggiunta rifiutata dal negozio (esaurito, mercato): niente tentativi per 2 minuti;
 *     errore di rete o di sovraccarico: nuovo tentativo dopo 10 s;
 *   - non pubblica mai cart-update (niente cicli con gli altri ascoltatori);
 *   - al Check-out rilegge e corregge prima di partire (anche col freno tirato) e
 *     dopo 4 s parte comunque; mentre un giro e' in corso i pagamenti rapidi del
 *     carrello sono disattivati (classe fg-in-corso sul body).
 *
 * Debug: localStorage 'reaven:free-gift:debug' = '1'; window.__reavenFreeGift.status().
 */
(() => {
  const configElement = document.getElementById('FreeGiftConfig');
  if (!configElement) return;

  let cfg;
  try {
    cfg = JSON.parse(configElement.textContent);
  } catch (e) {
    return;
  }
  if (!cfg || !cfg.productId || !cfg.variantId || cfg.designMode) return;

  const ATTR_THRESHOLD = '_fg_soglia';
  const ATTR_CURRENCY = '_fg_valuta';
  const FETCH_TIMEOUT = 6000;
  const MAX_WRITES = 6;
  const WRITE_WINDOW = 20000;
  const PAUSE = 120000;
  const SHORT_PAUSE = 10000;
  const CHECKOUT_WAIT = 4000;
  const HIDDEN_RECHECK = 5000;
  const VERIFY_DELAY = 1200;

  const state = {
    running: null,
    dirty: false,
    force: false,
    timer: null,
    writes: [],
    haltedUntil: 0,
    addFailedUntil: 0,
    addFailedHard: false,
    bypassCheckout: false,
    checkingOut: false,
    hiddenAt: 0,
    last: null,
  };

  const debug = (() => {
    try {
      return window.localStorage.getItem('reaven:free-gift:debug') === '1';
    } catch (e) {
      return false;
    }
  })();
  const log = (...args) => {
    if (debug) console.log('[free-gift]', ...args);
  };

  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const updateUrl = () => (window.routes && window.routes.cart_update_url) || '/cart/update';

  function fetchJson(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
    const headers = { Accept: 'application/json' };
    if (options.body) headers['Content-Type'] = 'application/json';
    return fetch(url, { cache: 'no-store', credentials: 'same-origin', ...options, headers, signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          const error = new Error((data && (data.description || data.message)) || `HTTP ${response.status}`);
          error.status = response.status;
          throw error;
        }
        return data;
      })
      .finally(() => clearTimeout(timer));
  }

  const isGift = (item) => item.product_id === cfg.productId;

  // Soglia nella valuta del carrello, in centesimi. null = sconosciuta (non si tocca il regalo).
  function presentmentThreshold(cart) {
    if (!cfg.threshold) return 0;
    if (cart.currency === cfg.shopCurrency) return cfg.threshold;
    const currency = window.Shopify && window.Shopify.currency;
    const rate = currency && currency.active === cart.currency ? parseFloat(currency.rate) : NaN;
    if (!rate || !isFinite(rate)) return null;
    return Math.ceil((cfg.threshold / 100) * rate) * 100;
  }

  function attributesToWrite(cart, threshold) {
    if (cart.currency === cfg.shopCurrency || !threshold || cart.item_count === 0) return null;
    const attributes = cart.attributes || {};
    if (attributes[ATTR_CURRENCY] === cart.currency && String(attributes[ATTR_THRESHOLD]) === String(threshold)) {
      return null;
    }
    return { [ATTR_CURRENCY]: cart.currency, [ATTR_THRESHOLD]: String(threshold) };
  }

  function decide(cart, threshold, force) {
    const giftLines = cart.items.filter(isGift);
    const paid = cart.items.filter((item) => !isGift(item) && item.requires_shipping && !item.gift_card);
    const sum = paid.reduce((total, item) => total + item.final_line_price, 0);
    const priceOk = giftLines.every((item) => item.original_price === 0 && item.final_price === 0);
    const active = cfg.enabled && cfg.productOk;

    if (active && threshold === null) {
      return { want: null, sum, threshold, giftLines: giftLines.length, needed: false, adding: false, updates: {}, unknown: true };
    }

    const eligible = active && threshold > 0 && paid.length > 0 && sum >= threshold;
    const want = eligible && priceOk ? 1 : 0;

    const updates = {};
    let needed = false;
    let adding = false;
    if (want === 1) {
      if (giftLines.length === 0) {
        const canRetry = force && !state.addFailedHard;
        if (Date.now() >= state.addFailedUntil || canRetry) {
          updates[cfg.variantId] = 1;
          needed = true;
          adding = true;
        }
      } else {
        giftLines.forEach((line, index) => {
          const quantity = index === 0 ? 1 : 0;
          if (line.quantity !== quantity) {
            updates[line.key] = quantity;
            needed = true;
          }
        });
      }
    } else {
      giftLines.forEach((line) => {
        updates[line.key] = 0;
        needed = true;
      });
    }
    return { want, sum, threshold, giftLines: giftLines.length, needed, adding, updates };
  }

  // ---- ridisegno delle sezioni del carrello -------------------------------------

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // Ricorda l'elemento col focus per riga (chiave del carrello) e nome, non per posizione:
  // quando il regalo entra o esce gli indici delle righe cambiano.
  function saveFocus(container) {
    const element = document.activeElement;
    if (!element || element === document.body || !container.contains(element)) return null;
    const row = element.closest('tr.cart-item');
    const keyInput = row && row.querySelector('[data-quantity-line-key]');
    return {
      id: element.id || null,
      key: keyInput ? keyInput.dataset.quantityLineKey : null,
      name: element.getAttribute('name'),
    };
  }

  function findFocus(container, saved) {
    if (!saved) return null;
    if (saved.key) {
      const input = [...container.querySelectorAll('[data-quantity-line-key]')].find(
        (field) => field.dataset.quantityLineKey === saved.key
      );
      const row = input && input.closest('tr.cart-item');
      if (row) return (saved.name && row.querySelector(`[name="${saved.name}"]`)) || input;
    }
    return saved.id ? document.getElementById(saved.id) : null;
  }

  function applyDrawer(html) {
    const doc = parse(html);
    const fresh = doc.querySelector('.drawer__inner');
    const current = document.querySelector('#CartDrawer .drawer__inner');
    if (!fresh || !current) return;
    const scroller = current.querySelector('cart-drawer-items');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const savedFocus = saveFocus(current);
    current.innerHTML = fresh.innerHTML;
    const drawer = document.querySelector('cart-drawer');
    const freshDrawer = doc.querySelector('cart-drawer');
    if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
    const newScroller = current.querySelector('cart-drawer-items');
    if (newScroller) newScroller.scrollTop = scrollTop;
    // il trap del focus di Dawn ricorda primo e ultimo elemento: dopo il ridisegno va rifatto
    if (savedFocus && drawer && drawer.classList.contains('active') && typeof trapFocus === 'function') {
      trapFocus(drawer, findFocus(current, savedFocus) || current);
    }
  }

  function applyJsContents(container, html) {
    const fresh = parse(html).querySelector('.js-contents');
    const target = container.querySelector('.js-contents');
    if (!fresh || !target) return;
    const savedFocus = saveFocus(target);
    target.innerHTML = fresh.innerHTML;
    const element = findFocus(target, savedFocus);
    if (element) element.focus({ preventScroll: true });
  }

  function applyBubble(html) {
    const fresh = parse(html).querySelector('.shopify-section');
    const target = document.getElementById('cart-icon-bubble');
    if (fresh && target) target.innerHTML = fresh.innerHTML;
  }

  function sectionTargets() {
    const targets = [];
    if (document.querySelector('#CartDrawer .drawer__inner')) {
      targets.push({ section: 'cart-drawer', apply: applyDrawer });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ section: 'cart-icon-bubble', apply: applyBubble });
    }
    const items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) targets.push({ section: items.dataset.id, apply: (html) => applyJsContents(items, html) });
    const footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      targets.push({ section: footer.dataset.id, apply: (html) => applyJsContents(footer, html) });
    }
    return targets.slice(0, 5);
  }

  function applySections(targets, sections, itemCount) {
    if (!sections) return;
    targets.forEach((target) => {
      const html = sections[target.section];
      if (typeof html === 'string') {
        try {
          target.apply(html);
        } catch (e) {
          log('ridisegno non riuscito', target.section, e);
        }
      }
    });
    if (typeof itemCount === 'number') {
      const empty = itemCount === 0;
      document.querySelector('cart-items')?.classList.toggle('is-empty', empty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    }
  }

  async function refreshSections() {
    const targets = sectionTargets();
    if (!targets.length) return;
    const ids = targets.map((target) => target.section).join(',');
    const sections = await fetchJson(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`);
    applySections(targets, sections);
  }

  // Lo schermo e' vecchio se righe, ordine o quantita' non sono quelli del carrello vero:
  // il modulo del Check-out manda updates[] per posizione.
  function domLooksStale(cart) {
    const keys = cart.items.map((item) => item.key);
    const quantities = new Map(cart.items.filter((item) => !isGift(item)).map((item) => [item.key, item.quantity]));
    const forms = [document.getElementById('CartDrawer-Form'), document.getElementById('cart')].filter(Boolean);
    return forms.some((form) => {
      const rows = [...form.querySelectorAll('tr.cart-item')];
      const rowKeys = rows.map((row) => {
        if (row.dataset.freeGiftLine) return row.dataset.freeGiftLine;
        const input = row.querySelector('[data-quantity-line-key]');
        return input ? input.dataset.quantityLineKey : null;
      });
      if (rowKeys.length !== keys.length || rowKeys.some((key, index) => key !== keys[index])) return true;
      return [...form.querySelectorAll('input[data-quantity-line-key]')].some(
        (input) => quantities.get(input.dataset.quantityLineKey) !== parseInt(input.getAttribute('value'), 10)
      );
    });
  }

  // ---- scritture ----------------------------------------------------------------

  function halted() {
    return Date.now() < state.haltedUntil;
  }

  function recordWrite() {
    const now = Date.now();
    state.writes = state.writes.filter((time) => now - time < WRITE_WINDOW);
    state.writes.push(now);
    if (state.writes.length >= MAX_WRITES) {
      state.haltedUntil = now + PAUSE;
      log('troppe scritture ravvicinate: pausa di 2 minuti');
      schedule('fine-pausa', PAUSE + 200);
    }
  }

  async function writeCart(payload) {
    const targets = sectionTargets();
    const body = {
      ...payload,
      sections: targets.map((target) => target.section),
      sections_url: window.location.pathname,
    };
    const cart = await fetchJson(`${updateUrl()}.js`, { method: 'POST', body: JSON.stringify(body) });
    applySections(targets, cart && cart.sections, cart && cart.item_count);
    return cart;
  }

  function liveRegion() {
    let region = document.getElementById('FreeGiftLive');
    if (!region) {
      region = document.createElement('div');
      region.id = 'FreeGiftLive';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    return region;
  }

  function announce(kind) {
    const region = liveRegion();
    region.textContent = '';
    setTimeout(() => {
      region.textContent = (cfg.strings && cfg.strings[kind]) || '';
    }, 60);
  }

  // Pagamenti rapidi del carrello (Shop Pay, PayPal, Google Pay) partono dal carrello del
  // server senza passare da qui: mentre un giro e' in attesa o in corso restano disattivati.
  function updateBusy() {
    if (document.body) document.body.classList.toggle('fg-in-corso', Boolean(state.running || state.timer));
  }

  // ---- giro -----------------------------------------------------------------------

  async function pass(force) {
    const cart = await fetchJson(`${cartUrl()}.js`);
    const threshold = presentmentThreshold(cart);
    const attributes = attributesToWrite(cart, threshold);
    const decision = decide(cart, threshold, force);
    state.last = { ...decision, updates: { ...decision.updates }, currency: cart.currency, at: new Date().toISOString() };
    log('decisione', state.last);

    if ((decision.needed || attributes) && (force || !halted())) {
      const payload = {};
      if (decision.needed) payload.updates = decision.updates;
      if (attributes) payload.attributes = attributes;
      try {
        const result = await writeCart(payload);
        recordWrite();
        if (decision.adding && !(result && result.items && result.items.some(isGift))) {
          state.addFailedUntil = Date.now() + PAUSE;
          state.addFailedHard = true;
          log('regalo non aggiunto dal negozio: pausa di 2 minuti');
        } else if (decision.needed) {
          if (decision.want === 1 && decision.giftLines === 0) announce('added');
          if (decision.want === 0 && decision.giftLines > 0) announce('removed');
        }
      } catch (error) {
        log('scrittura non riuscita', error);
        if (!decision.adding) throw error;
        // rifiuto vero del negozio (4xx) = pausa lunga; rete, 429 o 5xx = nuovo tentativo a breve
        const hard = Boolean(error.status && error.status >= 400 && error.status < 500 && error.status !== 429);
        state.addFailedHard = hard;
        state.addFailedUntil = Date.now() + (hard ? PAUSE : SHORT_PAUSE);
        if (!hard) schedule('riprova-aggiunta', SHORT_PAUSE + 100);
      }
      return true;
    }

    if (domLooksStale(cart)) {
      log('carrello a schermo diverso da quello vero: ridisegno');
      await refreshSections();
    }
    return false;
  }

  function run(reason, options = {}) {
    if (state.running) {
      state.dirty = true;
      if (options.force) state.force = true;
      return state.running;
    }
    state.force = Boolean(options.force);
    state.running = (async () => {
      let passes = 0;
      let wroteAny = false;
      try {
        do {
          state.dirty = false;
          const force = state.force;
          state.force = false;
          const wrote = await pass(force);
          wroteAny = wroteAny || wrote;
          passes += 1;
          if (!wrote && !state.dirty) break;
        } while (passes < 3);
      } catch (error) {
        log('giro interrotto', reason, error);
        if (state.dirty) schedule('riprova', 1500);
      } finally {
        state.running = null;
        updateBusy();
      }
      // una risposta in ritardo del tema puo' ridisegnare il carrello dopo di noi: un controllo in piu'
      if (wroteAny) setTimeout(() => run('verifica'), VERIFY_DELAY);
    })();
    updateBusy();
    return state.running;
  }

  function schedule(reason, delay = 150) {
    clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      state.timer = null;
      run(reason);
    }, delay);
    updateBusy();
  }

  // ---- Check-out: prima si mette in ordine il regalo, poi si parte -------------------

  function checkoutButtonFrom(target) {
    return target && target.closest ? target.closest('button[name="checkout"]') : null;
  }

  async function guardedCheckout(buttonId, formId) {
    state.checkingOut = true;
    const before = document.getElementById(buttonId);
    if (before) before.setAttribute('aria-busy', 'true');
    try {
      await Promise.race([run('checkout', { force: true }), new Promise((resolve) => setTimeout(resolve, CHECKOUT_WAIT))]);
    } catch (e) {
      log('controllo al check-out non riuscito, si parte comunque', e);
    }
    // Il giro puo' aver ridisegnato il carrello: bottone e form si cercano di nuovo.
    const button = document.getElementById(buttonId);
    const form = document.getElementById(formId) || (button && button.form);
    state.bypassCheckout = true;
    setTimeout(() => {
      state.bypassCheckout = false;
      state.checkingOut = false;
      [before, button].forEach((element) => element && element.removeAttribute('aria-busy'));
    }, 3000);
    try {
      if (form && button && button.form === form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
      } else if (form) {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'checkout';
        form.appendChild(field);
        form.submit();
      } else {
        window.location.href = '/checkout';
      }
    } catch (e) {
      // il carrello del server e' gia' stato corretto dal giro: si va al check-out comunque
      log('invio del modulo non riuscito, si va al check-out', e);
      window.location.href = '/checkout';
    }
  }

  // niente stopPropagation: gli altri ascoltatori del clic (tracciamenti) restano attivi
  function onCheckoutClick(event) {
    if (state.bypassCheckout) return;
    const button = checkoutButtonFrom(event.target);
    if (!button || button.disabled) return;
    const form = button.form;
    if (!form || !button.id || !form.id) return;
    event.preventDefault();
    if (state.checkingOut) return;
    guardedCheckout(button.id, form.id);
  }

  function onCheckoutSubmit(event) {
    if (state.bypassCheckout) return;
    const button = event.submitter;
    if (!button || button.name !== 'checkout' || !button.id || !event.target.id) return;
    event.preventDefault();
    if (state.checkingOut) return;
    guardedCheckout(button.id, event.target.id);
  }

  // ---- avvio ------------------------------------------------------------------------

  function wrapDrawerRender() {
    const DrawerClass = customElements.get('cart-drawer');
    if (!DrawerClass || DrawerClass.prototype.__freeGiftWrapped) return;
    const original = DrawerClass.prototype.renderContents;
    if (typeof original !== 'function') return;
    DrawerClass.prototype.renderContents = function (...args) {
      // i suggeriti stanno nella zona che scorre: dopo un'aggiunta dal + si resta dove si era
      const scroller = this.querySelector('cart-drawer-items');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const result = original.apply(this, args);
      const newScroller = this.querySelector('cart-drawer-items');
      if (newScroller && scrollTop) newScroller.scrollTop = scrollTop;
      schedule('drawer-render', 150);
      return result;
    };
    DrawerClass.prototype.__freeGiftWrapped = true;
  }

  // Il Liquid ha gia' calcolato tutto: al caricamento serve un giro solo se il carrello non e' a posto.
  function needsFirstRun() {
    if (!cfg.itemCount) return false;
    if (cfg.cartCurrency !== cfg.shopCurrency) return true;
    if (cfg.giftExpected) return !(cfg.giftRows === 1 && cfg.giftQty === 1);
    return cfg.giftRows > 0;
  }

  function init() {
    liveRegion();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        schedule('cart-update', 200);
      });
    }
    if (customElements.get('cart-drawer')) wrapDrawerRender();
    else customElements.whenDefined('cart-drawer').then(wrapDrawerRender);

    document.addEventListener('shopify:cart:view', () => schedule('cart-view', 60));
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) schedule('pagina-ripresa', 0);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        state.hiddenAt = Date.now();
      } else if (state.hiddenAt && Date.now() - state.hiddenAt > HIDDEN_RECHECK) {
        schedule('ritorno-scheda', 0);
      }
    });
    document.addEventListener('click', onCheckoutClick, true);
    document.addEventListener('submit', onCheckoutSubmit, true);

    if (needsFirstRun()) schedule('caricamento', 0);
  }

  window.__reavenFreeGift = {
    status: () => ({
      running: Boolean(state.running),
      haltedUntil: state.haltedUntil ? new Date(state.haltedUntil).toISOString() : null,
      addFailedUntil: state.addFailedUntil ? new Date(state.addFailedUntil).toISOString() : null,
      writesLast20s: state.writes.filter((time) => Date.now() - time < WRITE_WINDOW).length,
      last: state.last,
      config: cfg,
    }),
    run: () => run('manuale'),
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
